import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Boxes, Move3d, Sparkles, Loader2, Wand2, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

let mvRequested = false;
// Lazily load Google's <model-viewer> web component (once, only when a viewer is opened).
function ensureModelViewer() {
  if (mvRequested || (typeof customElements !== "undefined" && customElements.get("model-viewer"))) return;
  mvRequested = true;
  const s = document.createElement("script");
  s.type = "module";
  s.src = "https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js";
  document.head.appendChild(s);
}

// Interactive, auto-rotating 3D preview of a single generated character's .glb,
// with a "Remix this character" CTA that spins up a new game for the viewer.
export function Cast3DDialog({ model, gameId, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const open = !!model;
  useEffect(() => { if (open) ensureModelViewer(); }, [open]);

  const canRemix = !!(model?.glb && model?.asset_id && gameId);

  const remix = async () => {
    if (!user) { navigate("/register"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/games/remix-character", { source_game_id: gameId, asset_id: model.asset_id });
      if (data.challenge) toast.success(`You're in the “${data.challenge.title}” Remix Challenge! 🏆`);
      else toast.success(`‘${data.character}’ is now the hero of your new game! 🎮`);
      onClose();
      navigate(`/app/build/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't remix this character");
    } finally { setBusy(false); }
  };

  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const feature = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/challenges/feature", { source_game_id: gameId, asset_id: model.asset_id, days: 7 });
      toast.success(`“${data.title}” is now the weekly Remix Challenge! 🏆`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't feature this character");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden bg-[#0b0e16] border-[hsl(var(--border))]" data-testid="cast-3d-dialog">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <DialogTitle className="font-display text-lg font-bold text-white truncate flex items-center gap-2">
              <Boxes className="w-4 h-4 text-[#7c5cff] shrink-0" /> {model?.name}
            </DialogTitle>
            <div className="text-[11px] uppercase tracking-wide text-[#7c5cff] font-semibold mt-0.5">{model?.role} · AI-built with Meshy</div>
          </div>
          <span className="hidden sm:flex items-center gap-1 text-[11px] text-white/50 shrink-0"><Move3d className="w-3.5 h-3.5" /> Drag to rotate</span>
        </div>
        <div className="relative h-[400px] max-h-[54vh] bg-[radial-gradient(circle_at_50%_35%,#1a2136,#05060a)]">
          {model?.glb ? (
            <model-viewer
              key={model.glb}
              src={abs(model.glb)}
              poster={model.thumbnail}
              alt={model.name}
              auto-rotate=""
              camera-controls=""
              touch-action="pan-y"
              rotation-per-second="26deg"
              shadow-intensity="1"
              exposure="1.05"
              environment-image="neutral"
              interaction-prompt="none"
              style={{ width: "100%", height: "100%", backgroundColor: "transparent" }}
              data-testid="cast-3d-model-viewer"
            />
          ) : (
            <img src={model?.thumbnail} alt={model?.name} className="w-full h-full object-contain" />
          )}
        </div>
        {canRemix && (
          <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap bg-black/20">
            <p className="text-xs text-white/60 flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5 text-[#7c5cff]" /> Love this character? Make it the star of your own game.</p>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button onClick={feature} disabled={busy} variant="outline" data-testid="feature-challenge-btn" className="rounded-full gap-1.5 border-amber-400/40 text-amber-300 hover:text-amber-200">
                  <Trophy className="w-4 h-4" /> Feature
                </Button>
              )}
              <Button onClick={remix} disabled={busy} data-testid="remix-character-btn"
                className="rounded-full gap-1.5 bg-gradient-to-r from-[#1f6feb] to-[#7c3aed] text-white">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {user ? "Remix this character" : "Sign up to remix"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
